import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Fonts } from "../../constants/AppEnums";
import AppDialog from "../../components/AppBase/AppDialog";
import { useDispatch, useSelector } from "react-redux";
import { fetchError, showMessage } from "../../redux/actions";
import { theme } from "../../themes/theme";
import {
  getRoleBundleMappings,
  createRoleBundleMapping,
  updateRoleBundleMapping,
  deleteRoleBundleMapping,
} from "../../redux/actions/Role";
import { AppState } from "../../redux/store";
import { RoleState } from "../../types/models/RoleState";
import { RoleBundleMapping } from "../../types/models/LTOSetup";
import { RoleBundleMappingRequestBody } from "../../types/models/LTOSetupRequestBody";
import AppConfirmDialog from "../../components/AppBase/AppConfirmDialog";

interface LTOSetupDialogProp {
  isOpen: boolean;
  onClose: () => void;
  LTOSetup?: RoleBundleMapping;
}

const LTOSetupDialog: React.FC<LTOSetupDialogProp> = (prop) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formRoleBundleId, setFormRoleBundleId] = useState<string>("");
  const [formDisplayName, setFormDisplayName] = useState<string>("");
  const [formSkillIds, setFormSkillIds] = useState<string[]>([]);
  const [formOrganisation, setFormOrganisation] = useState<string>("");
  const [formRoleBundleError, setFormRoleBundleError] = useState<string>();
  const [formDisplayNameError, setFormDisplayNameError] = useState<string>();
  const [formSkillIdsError, setFormSkillIdsError] = useState<string>();
  const [formOrganisationError, setFormOrganisationError] = useState<string>();
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [mappingOption, setMappingOption] = useState<"SKILL" | "ORG">("SKILL");

  const roleState = useSelector<AppState, RoleState>((state) => state.role);

  useEffect(() => {
    if (prop.isOpen) {
      if (prop.LTOSetup) {
        setFormRoleBundleId(prop.LTOSetup.RoleBundleId);
        setFormDisplayName(prop.LTOSetup.Name);
        if (prop.LTOSetup.Organisation) {
          setMappingOption("ORG");
          setFormOrganisation(prop.LTOSetup.Organisation);
        } else {
          setMappingOption("SKILL");
          setFormSkillIds(prop.LTOSetup.SkillIds);
        }
        setEditMode(true);
      } else {
        clearErrors();
        clearForm();
      }
    }
  }, [prop.isOpen]);

  const handleSubmit = () => {
    if (!validateForm()) return;

    const setupRequest: RoleBundleMappingRequestBody = {
      Name: formDisplayName ?? "",
      RoleBundleId: formRoleBundleId ?? "",
      SkillIds: mappingOption === "SKILL" ? formSkillIds : [],
      Organisation: mappingOption === "ORG" ? formOrganisation : undefined,
    };
    setIsSubmitting(true);
    if (prop.LTOSetup) {
      dispatch(
        updateRoleBundleMapping(prop.LTOSetup.Id, setupRequest, (result) => {
          if (result) {
            // Refresh
            dispatch(
              getRoleBundleMappings(() => {
                setIsSubmitting(false);
                dispatch(showMessage("The LTO / Role Bundle Setup has been updated."));
                closeDialog();
              })
            );
          } else {
            setIsSubmitting(false);
            dispatch(fetchError("Failed to update the LTO / Role Bundle Setup."));
          }
        })
      );
    } else {
      dispatch(
        createRoleBundleMapping(setupRequest, (result) => {
          if (result) {
            // Refresh
            dispatch(
              getRoleBundleMappings(() => {
                setIsSubmitting(false);
                dispatch(showMessage("The LTO / Role Bundle Setup has been created."));
                closeDialog();
              })
            );
          } else {
            setIsSubmitting(false);
            dispatch(fetchError("Failed to create the LTO / Role Bundle Setup."));
          }
        })
      );
    }
  };

  const clearErrors = () => {
    setFormRoleBundleError(undefined);
    setFormDisplayNameError(undefined);
    setFormSkillIdsError(undefined);
    setFormOrganisationError(undefined);
  };

  const clearForm = () => {
    setFormRoleBundleId("");
    setFormDisplayName("");
    setFormOrganisation("");
    setFormSkillIds([]);
    setMappingOption("SKILL");
    setEditMode(false);
  };

  const closeDialog = () => {
    clearForm();
    clearErrors();
    prop.onClose();
  };

  const validateRoleBundle = (value: string | undefined) => {
    if (value && value.length > 0) {
      setFormRoleBundleError(undefined);
      return true;
    } else {
      setFormRoleBundleError("Please select role bundle");
      return false;
    }
  };
  const validateDisplayName = (value: string | undefined) => {
    if (value && value.length > 0 && value.length <= 256) {
      setFormDisplayNameError(undefined);
      return true;
    } else {
      setFormDisplayNameError("Please enter display name");
      return false;
    }
  };

  const validateOrganisation = (value: string | undefined) => {
    if (value && value.length > 0 && value.length <= 256) {
      setFormOrganisationError(undefined);
      return true;
    } else {
      setFormOrganisationError("Please enter organisation name");
      return false;
    }
  };

  const validateSkills = (value: string[] | undefined) => {
    if (value && value.length > 0) {
      setFormSkillIdsError(undefined);
      return true;
    } else {
      setFormSkillIdsError("Please select one or more skills");
      return false;
    }
  };

  const validateForm = () => {
    const result1 = validateRoleBundle(formRoleBundleId);
    const result2 = validateDisplayName(formDisplayName);
    let result3 = true;
    if (mappingOption === "SKILL") {
      result3 = validateSkills(formSkillIds);
    } else {
      result3 = validateOrganisation(formOrganisation);
    }
    return result1 && result2 && result3;
  };

  const handleChange = (field: string, value: string | string[] | undefined) => {
    switch (field) {
      case "RoleBundleId":
        setFormRoleBundleId(value as string);
        // Set default display name
        setFormDisplayName(getRoleBundleDisplayName(value as string));
        validateRoleBundle(value as string);
        break;
      case "DisplayName":
        setFormDisplayName(value as string);
        validateDisplayName(value as string);
        break;
      case "SkillIds":
        setFormSkillIds(value as string[]);
        validateSkills(value as string[]);
        break;
      case "Organisation":
        setFormOrganisation(value as string);
        validateOrganisation(value as string);
        break;
    }
  };

  const getRoleBundleDisplayName = (roleBundleId: string) => {
    const roleBundle = roleState.roleBundles?.find((bundle) => bundle.Id == roleBundleId);
    return roleBundle ? roleBundle.DisplayName : roleBundleId;
  };

  const getSkillDisplayName = (skillId: string) => {
    const skill = roleState.skillList?.find((s) => s.SkillId === skillId);
    return skill ? skillId + " - " + skill.DisplayName : skillId;
  };

  const handleDelete = () => {
    if (!prop.LTOSetup) return;
    setOpenDeleteConfirm(false);
    setIsSubmitting(true);
    dispatch(
      deleteRoleBundleMapping(prop.LTOSetup?.Id, (result) => {
        if (result) {
          // Refresh
          dispatch(
            getRoleBundleMappings(() => {
              setIsSubmitting(false);
              dispatch(showMessage("The LTO / Role Bundle Setup has been deleted."));
              prop.onClose();
            })
          );
        } else {
          setIsSubmitting(false);
          dispatch(fetchError("Failed to delete the LTO / Role Bundle Setup."));
        }
      })
    );
  };

  return (
    <>
      <AppDialog
        sxStyle={{ height: "105%", top: "-15px" }}
        open={prop.isOpen}
        dividers
        disableBackDropOffClose
        title={
          <Typography sx={{ mb: 1, fontWeight: Fonts.SEMI_BOLD }}>
            {prop.LTOSetup ? "Edit LTO / Role Bundle Setup" : "Create New LTO / Role Bundle Setup"}
          </Typography>
        }
        onClose={closeDialog}
      >
        <Box>
          {isSubmitting && (
            <Box
              sx={{
                position: "absolute",
                width: "90%",
                height: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size="3rem" sx={{ color: theme.palette.primary.main }} />
            </Box>
          )}
          <>
            <Box>
              <Box sx={{ px: 2, pt: 1, mb: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="role-bundle-select-label">Role Bundle</InputLabel>
                  <Select
                    disabled={editMode}
                    labelId="role-bundle-select-label"
                    label="Role Bundle"
                    value={formRoleBundleId}
                    onChange={(event) => handleChange("RoleBundleId", event.target.value)}
                  >
                    {roleState.roleBundles?.map((roleBundle) => (
                      <MenuItem key={roleBundle.Id} value={roleBundle.Id}>
                        <Box>{roleBundle.DisplayName}</Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {formRoleBundleError && (
                  <FormHelperText className="Mui-error">{formRoleBundleError}</FormHelperText>
                )}
              </Box>
              <Box sx={{ px: 2, py: 1 }}>
                <TextField
                  required
                  name="DisplayName"
                  value={formDisplayName}
                  variant="outlined"
                  fullWidth
                  id="DisplayName"
                  label="Display Name"
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => handleChange("DisplayName", event.target.value)}
                  error={formDisplayNameError ? true : undefined}
                  helperText={formDisplayNameError}
                />
              </Box>
              <Box sx={{ ml: 2, mt: 2 }}>
                <RadioGroup
                  value={mappingOption}
                  row
                  onChange={(event) =>
                    setMappingOption(event.target.value === "SKILL" ? "SKILL" : "ORG")
                  }
                >
                  <FormControlLabel value="SKILL" label="Skill" control={<Radio />} />
                  <FormControlLabel value="ORG" label="Organisation" control={<Radio />} />
                </RadioGroup>
              </Box>
              {mappingOption === "SKILL" && (
                <Box sx={{ px: 2, mt: 1, width: "100%" }}>
                  <FormControl sx={{ width: "100%" }}>
                    <Autocomplete
                      multiple
                      value={formSkillIds}
                      options={roleState.skillList?.map((skill) => skill.SkillId) ?? []}
                      getOptionLabel={(option) => getSkillDisplayName(option)}
                      renderInput={(params) => (
                        <TextField
                          sx={{ textTransform: "capitalize" }}
                          {...params}
                          label="Skills"
                        />
                      )}
                      onChange={(e, value) => handleChange("SkillIds", value)}
                      renderTags={(values: string[], getTagProps) =>
                        values.map((value, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={value}
                            label={getSkillDisplayName(value)}
                            clickable
                            deleteIcon={
                              <CancelIcon onMouseDown={(event) => event.stopPropagation()} />
                            }
                          />
                        ))
                      }
                    />
                    {formSkillIdsError && (
                      <FormHelperText className="Mui-error">{formSkillIdsError}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
              )}
              {mappingOption === "ORG" && (
                <Box sx={{ mx: 2, mt: 1 }}>
                  <TextField
                    name="Organisation"
                    value={formOrganisation}
                    variant="outlined"
                    fullWidth
                    id="Organisation"
                    label="Organisation"
                    InputLabelProps={{ shrink: true }}
                    onChange={(event) => handleChange("Organisation", event.target.value)}
                    error={formOrganisationError ? true : undefined}
                    helperText={formOrganisationError}
                  />
                </Box>
              )}
            </Box>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 4,
                mb: 1,
                px: 2,
              }}
            >
              {editMode ? (
                <Button
                  color="error"
                  sx={{
                    minWidth: 100,
                    textTransform: "none",
                  }}
                  variant="outlined"
                  disabled={isSubmitting}
                  onClick={() => setOpenDeleteConfirm(true)}
                  startIcon={<DeleteOutlineIcon />}
                >
                  Delete
                </Button>
              ) : (
                <Box />
              )}
              <Box>
                <Button
                  sx={{
                    minWidth: 100,
                    color: "white",
                    textTransform: "none",
                    mr: 2,
                  }}
                  variant="contained"
                  disabled={isSubmitting}
                  onClick={() => handleSubmit()}
                >
                  {prop.LTOSetup ? "Save Changes" : "Create Setup"}
                </Button>
                <Button
                  sx={{
                    minWidth: 100,
                    textTransform: "none",
                  }}
                  color="primary"
                  variant="outlined"
                  onClick={() => closeDialog()}
                >
                  Cancel
                </Button>
              </Box>
            </Stack>
          </>
        </Box>
      </AppDialog>
      <AppConfirmDialog
        open={openDeleteConfirm}
        onDeny={() => setOpenDeleteConfirm(false)}
        onConfirm={() => handleDelete()}
        title="Are you sure you want to delete the LTO / Role Bundle Setup?"
        dialogTitle="Delete LTO / Role Bundle Setup?"
      ></AppConfirmDialog>
    </>
  );
};

export default LTOSetupDialog;
